// client/src/components/pages/TelegramBI/ScheduleManager/ScheduleAnalyticsModal.js
import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaPlay,
  FaEnvelope,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { toApiDateString } from '../../../../utils/dateUtils';

const ScheduleAnalyticsModal = ({ schedule, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [recentFailures, setRecentFailures] = useState([]);

  useEffect(() => {
    if (schedule) {
      loadAnalytics();
    }
  }, [schedule]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load execution history for the last 14 days to compute analytics
      const response = await api.telegramBI.schedules.getHistory(schedule.schedule_id, {
        limit: 500,
        days: 14,
      });

      const data = response.data?.data || response.data || {};
      const executions = data.executions || (Array.isArray(data) ? data : []);

      // Compute stats from executions
      const totalExecutions = executions.length;
      const successCount = executions.filter((e) => e.success === true).length;
      const failedCount = executions.filter((e) => e.success === false).length;
      const successRate =
        totalExecutions > 0 ? ((successCount / totalExecutions) * 100).toFixed(1) : 0;
      const totalMessages = executions.reduce((sum, e) => sum + (e.messages_sent || 0), 0);
      const durations = executions.filter((e) => e.duration_ms).map((e) => e.duration_ms);
      const avgDuration =
        durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

      setStats({
        totalExecutions,
        successCount,
        failedCount,
        successRate: parseFloat(successRate),
        avgDurationMs: Math.round(avgDuration),
        totalMessages,
      });

      // Build daily data for bar chart (last 14 days)
      const dayMap = {};
      const now = new Date();
      for (let i = 13; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const key = toApiDateString(d);
        dayMap[key] = { date: key, total: 0, success: 0, failed: 0 };
      }

      executions.forEach((e) => {
        const key = e.execution_time ? toApiDateString(new Date(e.execution_time)) : null;
        if (key && dayMap[key]) {
          dayMap[key].total += 1;
          if (e.success === true) dayMap[key].success += 1;
          if (e.success === false) dayMap[key].failed += 1;
        }
      });

      setDailyData(Object.values(dayMap));

      // Recent failures
      const failures = executions.filter((e) => e.success === false && e.error_message).slice(0, 5);
      setRecentFailures(failures);
    } catch (error) {
      console.error('Error loading analytics:', error);
      // Set empty defaults on error
      setStats({
        totalExecutions: 0,
        successCount: 0,
        failedCount: 0,
        successRate: 0,
        avgDurationMs: 0,
        totalMessages: 0,
      });
      setDailyData([]);
      setRecentFailures([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return '--';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatShortDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const renderBarChart = () => {
    if (dailyData.length === 0) return null;

    const maxTotal = Math.max(...dailyData.map((d) => d.total), 1);

    return (
      <div className="bg-surface border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">
          Executions per Day (Last 14 days)
        </h3>
        <div className="flex items-end space-x-1" style={{ height: '140px' }}>
          {dailyData.map((day, idx) => {
            const barHeight = maxTotal > 0 ? (day.total / maxTotal) * 100 : 0;
            const successHeight = maxTotal > 0 ? (day.success / maxTotal) * 100 : 0;
            const failedHeight = maxTotal > 0 ? (day.failed / maxTotal) * 100 : 0;

            return (
              <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                {/* Tooltip */}
                <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white dark:text-gray-50 text-[10px] rounded px-2 py-1 whitespace-nowrap z-10">
                  {formatShortDate(day.date)}: {day.total} total ({day.success} ok, {day.failed}{' '}
                  fail)
                </div>
                {/* Stacked bars */}
                <div className="w-full flex flex-col justify-end" style={{ height: '120px' }}>
                  {day.failed > 0 && (
                    <div
                      className="w-full bg-red-400 rounded-t-sm"
                      style={{
                        height: `${failedHeight}%`,
                        minHeight: day.failed > 0 ? '2px' : '0',
                      }}
                    />
                  )}
                  {day.success > 0 && (
                    <div
                      className="w-full bg-primary-400 rounded-t-sm"
                      style={{
                        height: `${successHeight}%`,
                        minHeight: day.success > 0 ? '2px' : '0',
                      }}
                    />
                  )}
                  {day.total === 0 && (
                    <div className="w-full bg-gray-100 rounded-t-sm" style={{ height: '2px' }} />
                  )}
                </div>
                {/* Date label (show every other) */}
                {idx % 2 === 0 && (
                  <span className="text-[9px] text-gray-400 mt-1 transform -rotate-45 origin-top-left whitespace-nowrap">
                    {formatShortDate(day.date)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div className="flex items-center space-x-4 mt-4 text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <div className="w-2.5 h-2.5 bg-primary-400 rounded-sm" />
            <span>Success</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2.5 h-2.5 bg-red-400 rounded-sm" />
            <span>Failed</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-surface rounded-lg p-8 flex items-center space-x-3"
          onClick={(e) => e.stopPropagation()}
        >
          <FaSpinner className="animate-spin h-5 w-5 text-primary-500" />
          <span className="text-sm text-gray-700">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <FaChartLine className="text-primary-500" size={16} />
              <span>Schedule Analytics</span>
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{schedule?.schedule_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-surface border border-gray-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <FaPlay className="text-blue-500" size={14} />
                <span className="text-xs text-gray-500">Total Executions</span>
              </div>
              <p className="text-xl font-bold text-gray-800 mt-1">{stats?.totalExecutions ?? 0}</p>
            </div>

            <div className="bg-surface border border-gray-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <FaCheckCircle className="text-green-500" size={14} />
                <span className="text-xs text-gray-500">Success Rate</span>
              </div>
              <p
                className={`text-xl font-bold mt-1 ${
                  (stats?.successRate ?? 0) >= 95
                    ? 'text-green-600'
                    : (stats?.successRate ?? 0) >= 80
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}
              >
                {stats?.successRate ?? 0}%
              </p>
            </div>

            <div className="bg-surface border border-gray-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <FaClock className="text-purple-500" size={14} />
                <span className="text-xs text-gray-500">Avg Duration</span>
              </div>
              <p className="text-xl font-bold text-gray-800 mt-1">
                {formatDuration(stats?.avgDurationMs)}
              </p>
            </div>

            <div className="bg-surface border border-gray-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <FaEnvelope className="text-primary-500" size={14} />
                <span className="text-xs text-gray-500">Messages Sent</span>
              </div>
              <p className="text-xl font-bold text-gray-800 mt-1">{stats?.totalMessages ?? 0}</p>
            </div>
          </div>

          {/* Bar Chart */}
          {renderBarChart()}

          {/* Recent Failures */}
          <div className="bg-surface border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-1.5">
              <FaExclamationTriangle className="text-red-400" size={13} />
              <span>Recent Failures</span>
            </h3>
            {recentFailures.length === 0 ? (
              <div className="text-center py-6">
                <FaCheckCircle className="h-6 w-6 text-green-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No recent failures</p>
                <p className="text-xs text-gray-400">
                  All executions in the last 14 days succeeded
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentFailures.map((fail, idx) => (
                  <div
                    key={fail.schedule_execution_id || idx}
                    className="border border-red-100 bg-red-50 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-red-800">
                        {fail.execution_time
                          ? new Date(fail.execution_time).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'Unknown time'}
                      </span>
                      <span className="text-[10px] text-red-600 font-mono">
                        {formatDuration(fail.duration_ms)}
                      </span>
                    </div>
                    <p className="text-xs text-red-700 leading-relaxed">{fail.error_message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleAnalyticsModal;
